import React from 'react';
import Box from '../../Box/Box';
import Button from '../../Button/Button';
import Typography from '../../Typography/Typography';
import Iconography from '../../Iconography/Iconography';
import IconButton from '../../IconButton/IconButton';
import Checkbox from '../../Checkbox/Checkbox';
import Loader from '../../Loader/Loader';
import { FileUploadStatus } from '../types';
import styles from './DocumentCard.module.scss';

type Theme = 'dark' | 'light';

export type DocumentCardProps = {
  /** Test ID for the component */
  dataTestId: string;
  /** Theme variant */
  theme: Theme;
  /** File name to display (optional for empty state) */
  fileName?: string;
  /** File size in bytes */
  fileSize?: number;
  /** Upload status */
  status: FileUploadStatus;
  /** Upload progress (0-100) for uploading/retrying status */
  progress?: number;
  /** Error message if any */
  error?: string;
  /** Optional eyebrow text to display above the file name */
  eyebrow?: string;
  /** Label text for empty state */
  label?: string;
  /** Helper text for empty state */
  helperText?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Show checkbox */
  showCheckbox?: boolean;
  /** Callback when retry button is clicked (for error status) */
  onRetry?: () => void;
  /** Callback when remove button is clicked */
  onRemove?: () => void;
  /** Callback when document is clicked (for download) */
  onDownload?: () => void;
  /** Callback when upload button is clicked (for empty status) */
  onUpload?: () => void;
  /** Whether this is a pre-existing document (not newly uploaded) */
  isPreExisting?: boolean;
  /** Index for test IDs */
  index: number;
};

const DocumentCard: React.FC<DocumentCardProps> = ({
  dataTestId,
  theme,
  fileName,
  status,
  error,
  eyebrow,
  label,
  helperText,
  required = false,
  disabled = false,
  showCheckbox = false,
  onRetry,
  onRemove,
  onDownload,
  onUpload,
  isPreExisting = false,
  index,
}) => {
  const renderStatusIcon = () => {
    switch (status) {
      case 'uploading':
        return (
          <>
            <Loader size={24} theme={theme} />
            {onRemove && (
              <IconButton
                dataTestId={`${dataTestId}-remove-${index}`}
                buttonType="tertiary"
                size="small"
                theme={theme}
                handleClick={onRemove}
                aria-label="Remove file"
                icon={{ category: 'global', name: 'close' }}
                gaComponent="file-upload-remove"
              />
            )}
          </>
        );
      case 'retrying':
        return (
          <>
            <Iconography category="global" name="warning" theme={theme} color="warning-1" />
            {onRetry && (
              <IconButton
                dataTestId={`${dataTestId}-retry-${index}`}
                buttonType="tertiary"
                size="small"
                theme={theme}
                handleClick={onRetry}
                className={styles.retryButton}
                aria-label="Retry upload"
                icon={{ category: 'global', name: 'refresh' }}
                gaComponent="file-upload-retry"
              />
            )}
          </>
        );
      case 'success':
        return (
          <>
            <Iconography
              category="global"
              name="circle-checkmark-filled"
              theme={theme}
              color={isPreExisting ? 'white' : 'success-1'}
            />
            {onRemove && (
              <IconButton
                dataTestId={`${dataTestId}-remove-${index}`}
                buttonType="tertiary"
                size="small"
                theme={theme}
                color="error-1"
                handleClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                aria-label="Remove file"
                icon={{ category: 'global', name: 'trash' }}
                gaComponent="file-upload-remove-success"
              />
            )}
          </>
        );
      case 'error':
        return (
          <>
            <IconButton
              dataTestId={`${dataTestId}-remove-error-${index}`}
              buttonType="tertiary"
              size="small"
              theme={theme}
              color="error-1"
              handleClick={onRemove}
              aria-label="Remove file"
              icon={{ category: 'global', name: 'circle-close-filled' }}
              gaComponent="file-upload-remove-error"
            />
            <IconButton
              dataTestId={`${dataTestId}-retry-${index}`}
              buttonType="tertiary"
              size="small"
              theme={theme}
              color="neutral"
              handleClick={onRetry}
              className={styles.retryButton}
              aria-label="Retry upload"
              icon={{ category: 'global', name: 'refresh' }}
              gaComponent="file-upload-retry"
            />
          </>
        );
      case 'empty':
        return (
          <Box className={styles.emptyActions}>
            {helperText && (
              <Typography variant="body" size="extraSmall" theme={theme} color="gray-5">
                {helperText}
              </Typography>
            )}
            <Button
              color="neutral"
              type="secondary"
              size="small"
              label="Upload"
              gaComponent="file-upload-inline"
              dataTestId={`${dataTestId}-upload-button`}
              handleClick={onUpload}
              disabled={disabled}
              theme={theme}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  // Determine wrapper class based on status (for CSS specificity)
  const getWrapperClassName = () => {
    const classes = [styles[theme]];

    if (status === 'empty') classes.push(styles.emptyState);
    if (status === 'error') classes.push(styles.errorState);
    if (status === 'success') {
      if (isPreExisting) {
        classes.push(styles.preExistingState);
      } else {
        classes.push(styles.successState);
      }
    }
    if (status === 'uploading' || status === 'retrying') classes.push(styles.uploadingState);

    return classes.join(' ');
  };

  // Determine card class based on status
  const getCardClassName = () => {
    const classes = [styles.documentCard];

    if (status === 'success') classes.push(styles.clickable);

    return classes.join(' ');
  };

  const handleCardClick = () => {
    if (status === 'success' && onDownload) {
      onDownload();
    }
  };

  return (
    <div className={getWrapperClassName()}>
      <Box
        className={getCardClassName()}
        onClick={handleCardClick}
        data-testid={
          status === 'success' ? `${dataTestId}-document-${index}` : `${dataTestId}-file-${index}`
        }
      >
        <Box className={styles.documentCardLeft}>
          {showCheckbox && (
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                dataTestId={`${dataTestId}-document-checkbox-${index}`}
                gaComponent="file-upload-document-checkbox"
                theme={theme}
                isChecked={false}
                showValidation={false}
                logEvents={false}
              />
            </div>
          )}
          <Iconography
            category="global"
            name="file"
            theme={theme}
            color="white"
            className={styles.documentIcon}
          />
          <Box className={styles.documentInfo}>
            {eyebrow && (
              <Typography variant="eyebrow" size="small" theme={theme} color="gray-4">
                {eyebrow}
              </Typography>
            )}
            <Typography
              variant="body"
              size="small"
              theme={theme}
              color={status === 'empty' ? 'gray-3' : 'white'}
              className={styles.documentTitle}
            >
              {status === 'empty' ? `${label}${required ? '*' : ''}` : fileName}
            </Typography>
            {status === 'error' && error && (
              <Typography variant="body" size="extraSmall" theme={theme} color="error-1">
                {error}
              </Typography>
            )}
            {status === 'retrying' && error && (
              <Typography variant="body" size="extraSmall" theme={theme} color="warning-1">
                {error}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className={styles.documentCardRight}>
          {}
          {renderStatusIcon()}
        </Box>
      </Box>
    </div>
  );
};

export default DocumentCard;
